import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useToast } from "@core/hooks/use-toast";
import { logError } from "@core/lib/error-handling";
import { usePolls } from "@core/hooks/usePolls";
import { useConversations } from "@core/hooks/useConversations";
import { useAuth } from "@core/contexts/AuthContext";
import { supabase } from "@core/lib/supabase";
import {
  generatePDFHTML,
  generateMarkdownContent,
} from "@core/lib/export-generators";
import {
  getProductConfig,
  type ProductType,
} from "@core/config/products.config";

interface LocalDeletionWarning {
  type: "chat" | "poll" | "account";
  daysUntilDeletion: number;
  itemCount: number;
  deletionDate: Date;
}

// Valeurs RGPD-conformes depuis 2026-04-16.
// "6-years" retiré (disproportionné pour un sondage grand public).
// Purge v2 (2026-07-03) : "unlimited" = NULL en base, défaut pour les plans
// payants (la purge ne touche leurs polls que sur opt-in 12/24 mois).
interface DataControlSettings {
  chatRetention: "30-days" | "12-months";
  pollRetention: "unlimited" | "12-months" | "24-months";
  autoDeleteEnabled: boolean;
  emailNotifications: boolean;
  allowDataForImprovement: boolean;
}

interface FilteredData {
  pollCount: number;
  conversationCount: number;
  polls: any[];
  conversations: any[];
  labels: {
    itemName: string;
    itemPlural: string;
    conversationName: string;
    conversationPlural: string;
  };
}

export const useDataControl = (productType: ProductType) => {
  const { t } = useTranslation("common");
  const { toast } = useToast();
  const { user } = useAuth();
  const { polls } = usePolls();
  const { conversations } = useConversations();
  const hasEmailAccount = Boolean(user?.id && user?.email);
  const config = getProductConfig(productType);

  // État des paramètres
  const [settings, setSettings] = useState<DataControlSettings>({
    chatRetention: "30-days",
    pollRetention: "unlimited",
    autoDeleteEnabled: true,
    emailNotifications: true,
    allowDataForImprovement: false,
  });

  // État des suppressions à venir
  const [upcomingDeletions, setUpcomingDeletions] = useState<
    LocalDeletionWarning[]
  >([]);
  const [showPreview, setShowPreview] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<
    "json" | "csv" | "pdf" | "markdown"
  >("json");

  // Filtrer les données par produit
  const getFilteredData = useCallback((): FilteredData => {
    // Filtrer les polls par type
    const filteredPolls = polls.filter((poll) => {
      switch (productType) {
        case "date":
          return poll.type === "date";
        case "form":
          return poll.type === "form";
        case "quizz":
          return poll.type === "quizz";
        case "availability":
          return poll.type === "availability";
        default:
          return true; // Accueil: tous les polls
      }
    });

    // Pour les conversations, on utilise les tags si disponibles, sinon tout
    const filteredConversations = conversations.conversations.filter((conv) => {
      if (productType === "date") {
        const tags = conv.tags || [];
        return tags.some((tag: string) => tag.toLowerCase().includes("date"));
      }
      if (productType === "form") {
        const tags = conv.tags || [];
        return tags.some((tag: string) => tag.toLowerCase().includes("form"));
      }
      if (productType === "quizz") {
        const tags = conv.tags || [];
        return tags.some((tag: string) => tag.toLowerCase().includes("quiz"));
      }
      if (productType === "availability") {
        const tags = conv.tags || [];
        return tags.some((tag: string) =>
          tag.toLowerCase().includes("availability"),
        );
      }
      return true; // Accueil: toutes les conversations
    });

    // Adapter les termes selon le produit
    const getProductLabels = () => {
      switch (productType) {
        case "date":
          return {
            itemName: t("data_control.label.date_polls"),
            itemPlural: t("data_control.label.date_polls"),
            conversationName: t("data_control.label.ai_conversations"),
            conversationPlural: t("data_control.label.ai_conversations"),
          };
        case "form":
          return {
            itemName: t("data_control.label.forms"),
            itemPlural: t("data_control.label.forms"),
            conversationName: t("data_control.label.ai_conversations"),
            conversationPlural: t("data_control.label.ai_conversations"),
          };
        case "quizz":
          return {
            itemName: t("data_control.label.quiz"),
            itemPlural: t("data_control.label.quiz"),
            conversationName: t("data_control.label.ai_conversations"),
            conversationPlural: t("data_control.label.ai_conversations"),
          };
        case "availability":
          return {
            itemName: t("data_control.label.availability_polls"),
            itemPlural: t("data_control.label.availability_polls"),
            conversationName: t("data_control.label.ai_conversations"),
            conversationPlural: t("data_control.label.ai_conversations"),
          };
        default:
          return {
            itemName: t("data_control.label.polls"),
            itemPlural: t("data_control.label.polls"),
            conversationName: t("data_control.label.conversations"),
            conversationPlural: t("data_control.label.conversations"),
          };
      }
    };

    const labels = getProductLabels();

    return {
      pollCount: filteredPolls.length,
      conversationCount: filteredConversations.length,
      polls: filteredPolls,
      conversations: filteredConversations,
      labels,
    };
  }, [productType, polls, conversations.conversations, t]);

  const filteredData = getFilteredData();

  // Calculer les suppressions à venir
  const calculateUpcomingDeletions = useCallback(
    async (currentSettings: DataControlSettings) => {
      try {
        const warnings: LocalDeletionWarning[] = [];
        const now = new Date();

        if (currentSettings.autoDeleteEnabled) {
          warnings.push({
            type: "chat",
            daysUntilDeletion: 15,
            itemCount: filteredData.conversationCount,
            deletionDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000),
          });
        }

        if (
          currentSettings.autoDeleteEnabled &&
          currentSettings.pollRetention !== "unlimited"
        ) {
          warnings.push({
            type: "poll",
            daysUntilDeletion: 45,
            itemCount: filteredData.pollCount,
            deletionDate: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000),
          });
        }

        setUpcomingDeletions(warnings);
      } catch (error) {
        logError(new Error(`Erreur calcul suppressions: ${error}`));
        // Fallback avec données simulées
        const warnings: LocalDeletionWarning[] = [];
        const now = new Date();

        if (currentSettings.autoDeleteEnabled) {
          warnings.push({
            type: "chat",
            daysUntilDeletion: 15,
            itemCount: filteredData.conversationCount || 0,
            deletionDate: new Date(now.getTime() + 15 * 24 * 60 * 60 * 1000),
          });
        }

        if (currentSettings.autoDeleteEnabled) {
          warnings.push({
            type: "poll",
            daysUntilDeletion: 45,
            itemCount: filteredData.pollCount || 0,
            deletionDate: new Date(now.getTime() + 45 * 24 * 60 * 60 * 1000),
          });
        }

        setUpcomingDeletions(warnings);
      }
    },
    [filteredData.conversationCount, filteredData.pollCount],
  );

  // Charger les préférences au montage :
  //  - User connecté : depuis la table `profiles` (source de vérité)
  //  - Sinon : fallback localStorage (guest)
  // Migre automatiquement les anciennes valeurs ("indefinite" / "6-years")
  // vers les valeurs RGPD-conformes si on les détecte dans localStorage.
  useEffect(() => {
    let cancelled = false;

    async function loadSettings() {
      // Normalisation des anciennes valeurs localStorage (retro-compat)
      const rawChat = localStorage.getItem("doodates_chat_retention");
      const normalizedChat: DataControlSettings["chatRetention"] =
        rawChat === "12-months" ? "12-months" : "30-days"; // "indefinite" → "30-days"
      const rawPoll = localStorage.getItem("doodates_poll_retention");
      const normalizedPoll: DataControlSettings["pollRetention"] =
        rawPoll === "24-months" || rawPoll === "6-years" // ancien "6-years" → cap à 24
          ? "24-months"
          : rawPoll === "12-months"
            ? "12-months"
            : "unlimited"; // défaut purge v2 : pas de purge sans opt-in

      const localSettings: DataControlSettings = {
        chatRetention: normalizedChat,
        pollRetention: normalizedPoll,
        autoDeleteEnabled:
          localStorage.getItem("doodates_auto_delete") !== "false",
        emailNotifications:
          localStorage.getItem("doodates_email_notifications") !== "false",
        allowDataForImprovement:
          localStorage.getItem("doodates_allow_data_improvement") === "true",
      };

      // User non connecté → on s'arrête au localStorage
      if (!user?.id) {
        if (!cancelled) {
          setSettings(localSettings);
          calculateUpcomingDeletions(localSettings);
        }
        return;
      }

      // User connecté → on lit profiles et on merge
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select(
            "chat_retention, poll_retention, auto_delete_enabled, email_notifications, allow_data_for_improvement",
          )
          .eq("id", user.id)
          .maybeSingle();

        if (error) throw error;
        if (cancelled) return;

        const remote: DataControlSettings = {
          chatRetention:
            (data?.chat_retention as DataControlSettings["chatRetention"]) ??
            localSettings.chatRetention,
          // NULL en base = illimité (défaut purge v2), pas un champ manquant
          pollRetention:
            (data?.poll_retention as DataControlSettings["pollRetention"]) ??
            "unlimited",
          autoDeleteEnabled:
            data?.auto_delete_enabled ?? localSettings.autoDeleteEnabled,
          emailNotifications:
            data?.email_notifications ?? localSettings.emailNotifications,
          allowDataForImprovement:
            data?.allow_data_for_improvement ??
            localSettings.allowDataForImprovement,
        };
        setSettings(remote);
        calculateUpcomingDeletions(remote);
      } catch (e) {
        logError(
          new Error(
            `Failed to load data control settings from Supabase: ${
              e instanceof Error ? e.message : String(e)
            }`,
          ),
        );
        // Fallback gracieux sur localStorage
        if (!cancelled) {
          setSettings(localSettings);
          calculateUpcomingDeletions(localSettings);
        }
      }
    }

    loadSettings();
    return () => {
      cancelled = true;
    };
  }, [user?.id, calculateUpcomingDeletions]);

  const handleSettingChange = (
    key: keyof DataControlSettings,
    value: string | boolean,
  ) => {
    if (key === "emailNotifications" && !hasEmailAccount) {
      toast({
        title: t("data_control.account_required.title"),
        description: t("data_control.account_required.email_alerts"),
        duration: 3500,
      });
      return;
    }

    const newSettings = { ...settings, [key]: value };
    setSettings(newSettings);

    // 1. Toujours sauvegarder dans localStorage (backup + support guest)
    const storageKeys = {
      chatRetention: "doodates_chat_retention",
      pollRetention: "doodates_poll_retention",
      autoDeleteEnabled: "doodates_auto_delete",
      emailNotifications: "doodates_email_notifications",
      allowDataForImprovement: "doodates_allow_data_improvement",
    };
    if (
      key === "autoDeleteEnabled" ||
      key === "allowDataForImprovement" ||
      key === "emailNotifications"
    ) {
      localStorage.setItem(storageKeys[key], value.toString());
    } else {
      localStorage.setItem(storageKeys[key], value as string);
    }

    // 2. Si user connecté : persister aussi dans profiles (source de vérité).
    //    On fait un fire-and-forget avec log d'erreur, UI reste réactive.
    if (user?.id) {
      const dbKeyMap: Record<keyof DataControlSettings, string> = {
        chatRetention: "chat_retention",
        pollRetention: "poll_retention",
        autoDeleteEnabled: "auto_delete_enabled",
        emailNotifications: "email_notifications",
        allowDataForImprovement: "allow_data_for_improvement",
      };
      const dbColumn = dbKeyMap[key];
      // "unlimited" est stocké NULL en base (contrainte CHECK 12/24 mois)
      const dbValue =
        key === "pollRetention" && value === "unlimited" ? null : value;
      void supabase
        .from("profiles")
        .update({ [dbColumn]: dbValue, updated_at: new Date().toISOString() })
        .eq("id", user.id)
        .then(({ error }) => {
          if (error) {
            logError(
              new Error(
                `Failed to persist ${String(key)} to profiles: ${error.message}`,
              ),
            );
          }
        });
    }

    // Recalculer les suppressions
    calculateUpcomingDeletions(newSettings);

    if (key === "allowDataForImprovement") {
      const consentEntry = {
        value: Boolean(value),
        updatedAt: new Date().toISOString(),
        product: config.headerDisplayName,
        source: "data-control",
      };
      localStorage.setItem(
        "doodates_data_improvement_consent",
        JSON.stringify(consentEntry),
      );
      window.dispatchEvent(
        new CustomEvent("doodates:consent-updated", { detail: consentEntry }),
      );
    }

    // Feedback utilisateur
    const messages: Record<string, Record<string, string>> = {
      chatRetention: {
        "30-days": t("data_control.feedback.chat_30_days"),
        "12-months": t("data_control.feedback.chat_12_months"),
      },
      pollRetention: {
        unlimited: t("data_control.feedback.poll_unlimited"),
        "12-months": t("data_control.feedback.poll_12_months"),
        "24-months": t("data_control.feedback.poll_24_months"),
      },
      autoDeleteEnabled: {
        true: t("data_control.feedback.auto_delete_on"),
        false: t("data_control.feedback.auto_delete_off"),
      },
      emailNotifications: {
        true: t("data_control.feedback.email_on"),
        false: t("data_control.feedback.email_off"),
      },
      allowDataForImprovement: {
        true: t("data_control.feedback.improvement_on"),
        false: t("data_control.feedback.improvement_off"),
      },
    };

    toast({
      title: t("data_control.setting_updated.title"),
      description:
        messages[key]?.[value.toString()] ||
        t("data_control.setting_updated.default"),
      duration: 3000,
    });
  };

  const handleExportData = async (
    format: "json" | "csv" | "pdf" | "markdown",
  ) => {
    try {
      toast({
        title: t("data_control.export_in_progress.title"),
        description: t("data_control.export_in_progress.description", {
          format: format.toUpperCase(),
        }),
        duration: 2000,
      });

      const labels = filteredData.labels;

      // Importer les fonctions d'export selon le type de poll
      const { exportFormPollToCSV, exportDatePollToCSV } =
        await import("@core/lib/exports");
      const { getVotesByPollId } = await import("@core/lib/pollStorage");

      // Précharger tous les votes/réponses depuis Supabase pour éviter
      // le sous-comptage sur polls créés/reçus depuis un autre navigateur
      // (bug constaté 2026-04-20 : export ne lisait que localStorage).
      const pollIds = filteredData.polls.map((p: any) => p.id).filter(Boolean);
      const votesByPoll = new Map<string, any[]>();
      if (pollIds.length > 0) {
        try {
          const { data: supabaseVotes } = await supabase
            .from("poll_responses")
            .select("*")
            .in("poll_id", pollIds);
          (supabaseVotes || []).forEach((row: any) => {
            const pid = row.poll_id;
            if (!votesByPoll.has(pid)) votesByPoll.set(pid, []);
            votesByPoll.get(pid)!.push(row);
          });
        } catch (error) {
          console.warn(
            "Failed to preload votes from Supabase, falling back to localStorage",
            error,
          );
        }
      }

      if (format === "csv") {
        // Export CSV par type de poll
        const csvExports: string[] = [];

        if (productType === "date") {
          const datePolls = filteredData.polls.filter((p) => p.type === "date");
          datePolls.forEach((poll) => {
            try {
              const preloaded = votesByPoll.get((poll as any).id);
              exportDatePollToCSV(poll as any, preloaded as any);
            } catch (error) {
              console.warn(
                `Failed to export date poll ${(poll as any).id}:`,
                error,
              );
            }
          });
        }

        if (productType === "form") {
          const formPolls = filteredData.polls.filter((p) => p.type === "form");
          formPolls.forEach((poll) => {
            try {
              const preloaded = votesByPoll.get((poll as any).id);
              exportFormPollToCSV(poll as any, preloaded as any);
            } catch (error) {
              console.warn(
                `Failed to export form poll ${(poll as any).id}:`,
                error,
              );
            }
          });
        }

        toast({
          title: t("data_control.export_csv_done.title"),
          description: t("data_control.export_csv_done.description", {
            count: filteredData.polls.length,
            items: labels.itemPlural,
          }),
          duration: 3000,
        });
      } else if (format === "pdf") {
        // Export PDF - Générer HTML et utiliser window.print()
        const { getAllPolls } = await import("@core/lib/pollStorage");
        const { getConversations: getStoredConversations } =
          await import("@core/lib/storage/ConversationStorageSimple");
        const allPolls = getAllPolls();
        const allConversations = getStoredConversations();

        const exportedPolls =
          filteredData.polls.length > 0
            ? filteredData.polls
            : allPolls.filter((poll) => {
                switch (productType) {
                  case "date":
                    return poll.type === "date";
                  case "form":
                    return poll.type === "form";
                  case "quizz":
                    return poll.type === "quizz";
                  case "availability":
                    return poll.type === "availability";
                  default:
                    return true;
                }
              });

        const exportedConversations =
          filteredData.conversations.length > 0
            ? filteredData.conversations
            : allConversations.filter((conv) => {
                const tags = (conv.tags || []).map((tag: string) =>
                  tag.toLowerCase(),
                );
                if (tags.some((tag: string) => tag.includes(productType)))
                  return true;
                return tags.length === 0;
              });

        // Générer le contenu HTML pour PDF
        const htmlContent = generatePDFHTML({
          product: config.headerDisplayName,
          polls: exportedPolls,
          conversations: exportedConversations,
          exportDate: new Date().toISOString(),
          settings: settings,
        });

        // Créer une fenêtre temporaire pour l'impression
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          printWindow.document.write(htmlContent);
          printWindow.document.close();

          // Attendre que le contenu soit chargé puis imprimer
          printWindow.onload = () => {
            setTimeout(() => {
              printWindow.print();
              printWindow.close();
            }, 500);
          };
        } else {
          // Fallback: utiliser window.print directement
          const printContainer = document.createElement("div");
          printContainer.innerHTML = htmlContent;
          printContainer.style.position = "fixed";
          printContainer.style.top = "-9999px";
          printContainer.style.left = "-9999px";
          document.body.appendChild(printContainer);

          window.print();
          document.body.removeChild(printContainer);
        }

        toast({
          title: t("data_control.export_pdf_initiated.title"),
          description: t("data_control.export_pdf_initiated.description", {
            pollCount: exportedPolls.length,
            items: labels.itemPlural,
            conversationCount: exportedConversations.length,
            conversations: labels.conversationPlural,
          }),
          duration: 5000,
        });
      } else if (format === "markdown") {
        // Export Markdown
        const { getAllPolls } = await import("@core/lib/pollStorage");
        const { getConversations: getStoredConversations } =
          await import("@core/lib/storage/ConversationStorageSimple");
        const allPolls = getAllPolls();
        const allConversations = getStoredConversations();

        const exportedPolls =
          filteredData.polls.length > 0
            ? filteredData.polls
            : allPolls.filter((poll) => {
                switch (productType) {
                  case "date":
                    return poll.type === "date";
                  case "form":
                    return poll.type === "form";
                  case "quizz":
                    return poll.type === "quizz";
                  case "availability":
                    return poll.type === "availability";
                  default:
                    return true;
                }
              });

        const exportedConversations =
          filteredData.conversations.length > 0
            ? filteredData.conversations
            : allConversations.filter((conv) => {
                const tags = (conv.tags || []).map((tag: string) =>
                  tag.toLowerCase(),
                );
                if (tags.some((tag: string) => tag.includes(productType)))
                  return true;
                return tags.length === 0;
              });

        // Générer le contenu Markdown
        const markdownContent = generateMarkdownContent({
          product: config.headerDisplayName,
          polls: exportedPolls,
          conversations: exportedConversations,
          exportDate: new Date().toISOString(),
          settings: settings,
        });

        // Télécharger le fichier Markdown
        const blob = new Blob([markdownContent], {
          type: "text/markdown",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `doodates-export-${config.headerDisplayName.toLowerCase().replace(" ", "-")}-${new Date().toISOString().split("T")[0]}.md`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: t("data_control.export_markdown_done.title"),
          description: t("data_control.export_markdown_done.description", {
            pollCount: exportedPolls.length,
            items: labels.itemPlural,
            conversationCount: exportedConversations.length,
            conversations: labels.conversationPlural,
          }),
          duration: 3000,
        });
      } else {
        // Export JSON (existant)
        const { getAllPolls } = await import("@core/lib/pollStorage");
        const { getConversations: getStoredConversations } =
          await import("@core/lib/storage/ConversationStorageSimple");
        const allPolls = getAllPolls();
        const allConversations = getStoredConversations();

        const exportedPolls =
          filteredData.polls.length > 0
            ? filteredData.polls
            : allPolls.filter((poll) => {
                switch (productType) {
                  case "date":
                    return poll.type === "date";
                  case "form":
                    return poll.type === "form";
                  case "quizz":
                    return poll.type === "quizz";
                  case "availability":
                    return poll.type === "availability";
                  default:
                    return true;
                }
              });

        const exportedConversations =
          filteredData.conversations.length > 0
            ? filteredData.conversations
            : allConversations.filter((conv) => {
                const tags = (conv.tags || []).map((tag: string) =>
                  tag.toLowerCase(),
                );
                if (tags.some((tag: string) => tag.includes(productType)))
                  return true;
                return tags.length === 0;
              });

        // Export JSON
        const exportData = {
          product: config.headerDisplayName,
          exportDate: new Date().toISOString(),
          [labels.itemPlural]: exportedPolls.map((poll) => ({
            id: (poll as any).id || "",
            title: poll.title,
            type: poll.type,
            createdAt:
              (poll as any).createdAt || (poll as any).created_at || "",
            settings: (poll as any).settings,
            votesCount:
              poll.type === "date"
                ? getVotesByPollId?.(poll.id)?.length || 0
                : undefined,
          })),
          [labels.conversationPlural]: exportedConversations.map((conv) => ({
            id: conv.id,
            title: conv.title,
            createdAt: conv.createdAt,
            messageCount: conv.messages?.length || 0,
            tags: conv.tags,
          })),
          settings: settings,
        };

        const blob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: "application/json",
        });

        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `doodates-export-${config.headerDisplayName.toLowerCase().replace(" ", "-")}-${new Date().toISOString().split("T")[0]}.json`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        toast({
          title: t("data_control.export_json_done.title"),
          description: t("data_control.export_json_done.description", {
            pollCount: exportedPolls.length,
            items: labels.itemPlural,
            conversationCount: exportedConversations.length,
            conversations: labels.conversationPlural,
          }),
          duration: 3000,
        });
      }

      setShowExportModal(false);
    } catch (error) {
      logError(new Error(`Erreur export données: ${error}`));
      toast({
        title: t("data_control.export_error.title"),
        description: t("data_control.export_error.description"),
        duration: 3000,
      });
    }
  };

  // Ouvre la modal de double confirmation. La suppression effective
  // est gérée par DeleteAccountModal → edge function `delete-account`.
  // Cf. docs/specifications/pr4-suppression-compte.md
  const handleDeleteAccount = () => {
    if (!hasEmailAccount) {
      toast({
        title: t("data_control.account_required.title"),
        description: t("data_control.account_required.delete_account"),
        duration: 3500,
      });
      return;
    }
    setShowDeleteModal(true);
  };

  const postponeDeletion = async (type: string) => {
    try {
      toast({
        title: t("data_control.postpone_done.title"),
        description:
          type === "chat"
            ? t("data_control.postpone_done.chat")
            : t("data_control.postpone_done.poll"),
        duration: 3000,
      });

      // Recalculer les alertes
      calculateUpcomingDeletions(settings);
    } catch (error) {
      logError(new Error(`Erreur report suppression: ${error}`));
      toast({
        title: t("data_control.postpone_error.title"),
        description: t("data_control.postpone_error.description"),
        duration: 3000,
      });
    }
  };

  const copyDpoEmail = async () => {
    try {
      await navigator.clipboard.writeText("privacy@doodates.com");
      toast({
        title: t("data_control.dpo_copied.title"),
        description: t("data_control.dpo_copied.description"),
        duration: 2500,
      });
    } catch {
      toast({
        title: t("data_control.dpo_copy_failed.title"),
        description: t("data_control.dpo_copy_failed.description"),
        duration: 3500,
      });
    }
  };

  return {
    // État
    settings,
    filteredData,
    upcomingDeletions,
    showPreview,
    showExportModal,
    showDeleteModal,
    exportFormat,
    hasEmailAccount,
    config,

    // Actions
    setSettings,
    setShowPreview,
    setShowExportModal,
    setShowDeleteModal,
    setExportFormat,

    // Handlers
    handleSettingChange,
    handleExportData,
    handleDeleteAccount,
    postponeDeletion,
    copyDpoEmail,
  };
};

export type { DataControlSettings, FilteredData, LocalDeletionWarning };
